import { useParams, useNavigate } from "react-router-dom";
import { useDepartmentById } from "@/hooks/useConfig";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Building2, User, Users, Loader2 } from "lucide-react";
import { DepartmentMember } from "@/lib/types";

function getDisplayName(m: DepartmentMember) {
  return m.profile?.name || m.email || m.id;
}

/** Leader from API may be flat or nested { user: { id, email, profile } } */
function normalizeLeader(leader: DepartmentMember | { user?: DepartmentMember }) {
  return (leader as { user?: DepartmentMember }).user ?? leader;
}

export function DepartmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: department, isLoading, error } = useDepartmentById(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !department) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/settings")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Settings
        </Button>
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Department not found or failed to load.
          </CardContent>
        </Card>
      </div>
    );
  }

  const createdDate =
    (department as { createdAt?: string; created_at?: string }).createdAt ??
    (department as { created_at?: string }).created_at;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/settings")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Settings
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">{department.name}</CardTitle>
                <CardDescription>
                  Department details · {department.agents?.length ?? 0} agents · {department.ticketCount ?? 0} tickets
                </CardDescription>
              </div>
            </div>
            <Badge variant={department.isActive !== false ? "default" : "secondary"}>
              {department.isActive !== false ? "Active" : "Inactive"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-muted-foreground">Created</span>
              <span>
                {createdDate ? new Date(createdDate).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—"}
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-muted-foreground">Ticket count</span>
              <span>{department.ticketCount ?? 0}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Team Leaders
          </CardTitle>
          <CardDescription>Assigned team leaders for this department</CardDescription>
        </CardHeader>
        <CardContent>
          {(() => {
            const leaders = department.teamLeaders ?? (department.teamLeader ? [department.teamLeader] : []);
            if (leaders.length === 0) {
              return <p className="text-muted-foreground">No team leaders assigned.</p>;
            }
            return (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaders.map((leader) => {
                    const u = normalizeLeader(leader) as DepartmentMember;
                    return (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">{getDisplayName(u)}</TableCell>
                        <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            );
          })()}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Agents
          </CardTitle>
          <CardDescription>Agents assigned to this department</CardDescription>
        </CardHeader>
        <CardContent>
          {department.agents && department.agents.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {department.agents.map((agent) => (
                  <TableRow key={agent.id}>
                    <TableCell className="font-medium">{getDisplayName(agent)}</TableCell>
                    <TableCell className="text-muted-foreground">{agent.email}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground">No agents assigned.</p>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button variant="outline" onClick={() => navigate("/admin/settings")}>
          Back to Config
        </Button>
      </div>
    </div>
  );
}
